import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlParams';
import { bikeModelAPI, BASE_URL } from '../services/api';
import './Brands.css';
import SearchableDropdown from '../components/SearchableDropdown';

function BikeModels() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [previewModels, setPreviewModels] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingModel, setSyncingModel] = useState(null);
  const [selectedBrandForSync, setSelectedBrandForSync] = useState('');
  const [previewBrandInfo, setPreviewBrandInfo] = useState(null);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({
    brandId: '',
    name: '',
    priceRange: '',
    price: '',
    rating: '',
    reviewCount: '',
    engine: '',
    mileage: '',
    power: '',
    topSpeed: '',
    fuelType: '',
    bodyType: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch brands for dropdown
  const fetchBrands = async () => {
    try {
      const response = await bikeModelAPI.getBrands();
      setBrands(response.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const fetchModels = async (currentPage, currentLimit, currentSearch, currentBrandId) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentBrandId) params.brandId = currentBrandId;

      const response = await bikeModelAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setModels(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setModels(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch bike models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchModels(page, limit, search, brandFilter);
  }, [page, limit, search, brandFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bike model?')) {
      try {
        await bikeModelAPI.delete(id);
        fetchModels(page, limit, search, brandFilter);
      } catch (err) {
        setError('Failed to delete bike model');
      }
    }
  };

  // Form modal handlers
  const handleCreate = () => {
    setEditingModel(null);
    setFormData({
      brandId: brandFilter || '',
      name: '',
      priceRange: '',
      price: '',
      rating: '',
      reviewCount: '',
      engine: '',
      mileage: '',
      power: '',
      topSpeed: '',
      fuelType: '',
      bodyType: ''
    });
    setShowFormModal(true);
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      brandId: model.brandId || '',
      name: model.name || '',
      priceRange: model.priceRange || '',
      price: model.price || '',
      rating: model.rating || '',
      reviewCount: model.reviewCount || '',
      engine: model.engine || '',
      mileage: model.mileage || '',
      power: model.power || '',
      topSpeed: model.topSpeed || '',
      fuelType: model.fuelType || '',
      bodyType: model.bodyType || ''
    });
    setShowFormModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brandId || !formData.name) {
      setError('Brand and Name are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editingModel) {
        await bikeModelAPI.update(editingModel.id, formData);
      } else {
        await bikeModelAPI.create(formData);
      }
      setShowFormModal(false);
      setEditingModel(null);
      fetchModels(page, limit, search, brandFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bike model');
    } finally {
      setFormLoading(false);
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingModel(null);
  };

  const handleSync = () => {
    setShowSyncModal(true);
    setSyncError('');
    setPreviewModels([]);
    setSelectedBrandForSync('');
    setPreviewBrandInfo(null);
  };

  const handlePreviewModels = async () => {
    if (!selectedBrandForSync) {
      setSyncError('Please select a brand first');
      return;
    }

    setLoadingPreview(true);
    setSyncError('');
    setPreviewModels([]);

    try {
      const response = await bikeModelAPI.preview({ brandSlug: selectedBrandForSync });
      setPreviewModels(response.data.models || []);
      setPreviewBrandInfo(response.data.brand || null);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to fetch models from BikeDekho');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSyncSingle = async (model) => {
    setSyncingModel(model.slug);
    try {
      await bikeModelAPI.syncSingle({
        modelId: model.modelId,
        brandId: model.brandId,
        brandSlug: model.brandSlug,
        name: model.name,
        slug: model.slug,
        modelShortName: model.modelShortName,
        image: model.image,
        priceRange: model.priceRange,
        price: model.price,
        rating: model.rating,
        reviewCount: model.reviewCount,
        modelUrl: model.modelUrl,
        modelSpecsURL: model.modelSpecsURL,
        modelPictureURL: model.modelPictureURL
      });
      // Update the model in preview list to show as existing
      setPreviewModels(prev => prev.map(m =>
        m.slug === model.slug ? { ...m, existsInDb: true } : m
      ));
      // Refresh the main models list
      fetchModels(page, limit, search, brandFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync model');
    } finally {
      setSyncingModel(null);
    }
  };

  const handleSyncAll = async () => {
    if (!selectedBrandForSync) {
      setSyncError('Please select a brand first');
      return;
    }

    setSyncing(true);
    try {
      await bikeModelAPI.sync({ brandSlug: selectedBrandForSync });
      // Refresh preview to show all as existing
      const response = await bikeModelAPI.preview({ brandSlug: selectedBrandForSync });
      setPreviewModels(response.data.models || []);
      setPage(1);
      fetchModels(1, limit, search, brandFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync all models');
    } finally {
      setSyncing(false);
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setPreviewModels([]);
    setSyncError('');
    setSelectedBrandForSync('');
    setPreviewBrandInfo(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleBrandFilterChange = (value) => {
    setBrandFilter(value);
    setPage(1);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Create/Edit Form Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingModel ? 'Edit Bike Model' : 'Add Bike Model'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Brand *</label>
                  <select
                    name="brandId"
                    value={formData.brandId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.brandName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Model Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., Splendor Plus, Pulsar 150"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Price Range</label>
                    <input
                      type="text"
                      name="priceRange"
                      value={formData.priceRange}
                      onChange={handleFormChange}
                      placeholder="e.g., Rs. 70,000 - 85,000"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleFormChange}
                      placeholder="e.g., Rs. 75,000"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rating</label>
                    <input
                      type="number"
                      name="rating"
                      value={formData.rating}
                      onChange={handleFormChange}
                      placeholder="e.g., 4.5"
                      step="0.1"
                      min="0"
                      max="5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Review Count</label>
                    <input
                      type="number"
                      name="reviewCount"
                      value={formData.reviewCount}
                      onChange={handleFormChange}
                      placeholder="e.g., 150"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Engine</label>
                    <input
                      type="text"
                      name="engine"
                      value={formData.engine}
                      onChange={handleFormChange}
                      placeholder="e.g., 124.7 cc"
                    />
                  </div>
                  <div className="form-group">
                    <label>Mileage</label>
                    <input
                      type="text"
                      name="mileage"
                      value={formData.mileage}
                      onChange={handleFormChange}
                      placeholder="e.g., 65 kmpl"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Power</label>
                    <input
                      type="text"
                      name="power"
                      value={formData.power}
                      onChange={handleFormChange}
                      placeholder="e.g., 10.9 bhp"
                    />
                  </div>
                  <div className="form-group">
                    <label>Top Speed</label>
                    <input
                      type="text"
                      name="topSpeed"
                      value={formData.topSpeed}
                      onChange={handleFormChange}
                      placeholder="e.g., 90 kmph"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Type</label>
                    <select
                      name="fuelType"
                      value={formData.fuelType}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Fuel Type</option>
                      <option value="Petrol">Petrol</option>
                      <option value="Electric">Electric</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Body Type</label>
                    <select
                      name="bodyType"
                      value={formData.bodyType}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Body Type</option>
                      <option value="Commuter">Commuter</option>
                      <option value="Sports">Sports</option>
                      <option value="Cruiser">Cruiser</option>
                      <option value="Adventure">Adventure</option>
                      <option value="Scooter">Scooter</option>
                      <option value="Naked">Naked</option>
                      <option value="Touring">Touring</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingModel ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Bike Models</h2>
              <button className="modal-close" onClick={closeSyncModal}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              <div className="sync-brand-selector">
                <label>Select Brand to Sync:</label>
                <div className="sync-brand-row">
                  <select
                    value={selectedBrandForSync}
                    onChange={(e) => setSelectedBrandForSync(e.target.value)}
                    className="brand-select"
                  >
                    <option value="">-- Select Brand --</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.brandSlug}>{brand.brandName}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    onClick={handlePreviewModels}
                    disabled={!selectedBrandForSync || loadingPreview}
                  >
                    {loadingPreview ? 'Loading...' : 'Load Models'}
                  </button>
                </div>
              </div>

              {previewBrandInfo && (
                <div className="preview-brand-header">
                  <h3>Models for {previewBrandInfo.name}</h3>
                  <span className="model-count">{previewModels.length} models found</span>
                </div>
              )}

              {loadingPreview && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading models from BikeDekho...</p>
                </div>
              )}
              {syncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <h3>Error</h3>
                  <p>{syncError}</p>
                </div>
              )}
              {!loadingPreview && !syncError && previewModels.length > 0 && (
                <div className="preview-brands-grid preview-models-grid">
                  {previewModels.map((model) => (
                    <div key={model.slug} className="preview-brand-card preview-model-card">
                      <div className="preview-brand-image preview-model-image">
                        {model.image ? (
                          <img
                            src={model.image.startsWith('http') ? model.image : `https://cdn.bikedekho.com${model.image}`}
                            alt={model.name}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="preview-logo-placeholder">
                            {model.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="preview-brand-info preview-model-info">
                        <h4>{model.name}</h4>
                        {model.priceRange && (
                          <span className="model-price">{model.priceRange}</span>
                        )}
                        {model.price && !model.priceRange && (
                          <span className="model-price">{model.price}</span>
                        )}
                      </div>
                      <button
                        className={`btn btn-sm ${model.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingle(model)}
                        disabled={syncingModel === model.slug}
                      >
                        {syncingModel === model.slug ? 'Syncing...' : (model.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeSyncModal}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAll}
                disabled={syncing || loadingPreview || !selectedBrandForSync || previewModels.length === 0}
              >
                {syncing ? 'Syncing All...' : 'Sync All Models'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Bike Models</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Model
          </button>
          <button
            className="btn btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by model name..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <SearchableDropdown
              options={brands.map(brand => ({ value: String(brand.id), label: brand.brandName }))}
              value={String(brandFilter || '')}
              onChange={handleBrandFilterChange}
              placeholder="Search brands..."
              allLabel="All Brands"
            />
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : models.length === 0 ? (
          <div className="empty-state">
            <p>No bike models found. {search || brandFilter ? 'Try adjusting your filters.' : 'Sync from BikeDekho to get started!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Brand</th>
                  <th>Model Name</th>
                  <th>Price Range</th>
                  <th>Rating</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.id}>
                    <td>{model.id}</td>
                    <td>
                      <div className="brand-logo model-image">
                        {model.image ? (
                          <img
                            src={getImageUrl(model.image)}
                            alt={model.name}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className="logo-placeholder" style={{ display: model.image ? 'none' : 'flex' }}>
                          {model.name.charAt(0)}
                        </div>
                      </div>
                    </td>
                    <td>{model.brand?.brandName || '-'}</td>
                    <td>{model.name}</td>
                    <td>{model.priceRange || model.price || '-'}</td>
                    <td>{model.rating ? `${model.rating}/5` : '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-view"
                          onClick={() => navigate(`/bike-models/${model.id}`)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(model)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(model.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .form-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 550px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .form-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px 24px;
          border-radius: 12px 12px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }
        .form-modal .modal-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
        }
        .form-modal .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .form-modal .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .form-modal .modal-body {
          padding: 24px;
        }
        .form-modal .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
      `}</style>
    </div>
  );
}

export default BikeModels;
